/**
 * EPIC-DM01: Demo Mode Hook
 * Provides access to demo mode configuration, session management, and intake processing
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { callEdgeFunction } from '@/lib/edgeFunctionClient';

// Types
export interface DemoConfig {
  enabled: boolean;
  dailyLimitPerHost: number;
  maxSwings: number;
  maxAnalysesPerDay: number;
  shareEnabled: boolean;
}

export interface DemoEvent {
  id: string;
  name: string;
  location: string | null;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

export interface DemoQuota {
  used: number;
  limit: number;
  remaining: number;
}

export interface DemoUser {
  id: string;
  isAdmin: boolean;
  role: string | null;
  isProspect?: boolean;
}

export interface DemoConfigResponse {
  config: DemoConfig;
  events: DemoEvent[];
  quota: DemoQuota;
  user: DemoUser;
}

export interface DemoSession {
  id: string;
  demo_event_id: string | null;
  demo_host_user_id: string;
  prospect_user_id: string | null;
  status: string;
  last_stage: string | null;
  started_at: string;
  updated_at: string;
  meta: Record<string, unknown>;
}

export interface CreateSessionResponse {
  session: DemoSession;
  quota: DemoQuota;
}

export interface IntakePayload {
  demo_session_id: string;
  name: string;
  email: string;
  handicap?: number;
  accepted_terms: boolean;
  terms_version: string;
  accepted_waiver: boolean;
  waiver_version: string;
  allow_social_sharing?: boolean;
  social_handles?: {
    instagram?: string;
    x?: string;
    tiktok?: string;
    facebook?: string;
  };
}

export interface IntakeResponse {
  ok: boolean;
  prospect_user_id: string;
  linked_existing: boolean;
}

// Hook
export function useDemoMode() {
  const queryClient = useQueryClient();

  // Fetch demo configuration
  const {
    data: configData,
    isLoading: isLoadingConfig,
    error: configError,
    refetch: refetchConfig,
  } = useQuery<DemoConfigResponse>({
    queryKey: ['demo-config'],
    queryFn: () => callEdgeFunction<DemoConfigResponse>('demo-config'),
    staleTime: 30_000, // 30 seconds
    retry: 1,
  });

  const retryDelay = (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 8000);

  // Create session mutation
  const createSessionMutation = useMutation({
    mutationFn: async (eventId?: string) => {
      return callEdgeFunction<CreateSessionResponse>(
        'demo-sessions',
        'POST',
        eventId ? { demo_event_id: eventId } : {}
      );
    },
    retry: 2,
    retryDelay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-config'] });
      queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
    },
  });

  // Update session mutation
  const updateSessionMutation = useMutation({
    mutationFn: async (payload: {
      session_id: string;
      status?: string;
      last_stage?: string;
      prospect_user_id?: string;
      meta?: Record<string, unknown>;
    }) => {
      return callEdgeFunction<{ session: DemoSession }>(
        'demo-sessions',
        'PATCH',
        payload
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
    },
  });

  // Submit intake mutation
  const submitIntakeMutation = useMutation({
    mutationFn: async (payload: IntakePayload) => {
      return callEdgeFunction<IntakeResponse>('demo-intake', 'POST', payload);
    },
    retry: 2,
    retryDelay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
    },
  });

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: async (payload: { name: string; location?: string; starts_at?: string; ends_at?: string }) => {
      const { data, error } = await supabase
        .from('demo_events')
        .insert({
          name: payload.name,
          location: payload.location || null,
          starts_at: payload.starts_at || null,
          ends_at: payload.ends_at || null,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-config'] });
    },
  });

  // Get session by ID
  const getSession = useCallback(async (sessionId: string): Promise<DemoSession | null> => {
    try {
      const params = new URLSearchParams({ id: sessionId });
      const result = await callEdgeFunction<{ session: DemoSession }>(
        'demo-sessions',
        'GET',
        undefined,
        params
      );
      return result.session;
    } catch {
      return null;
    }
  }, []);

  // DM01-C: Select swing for analysis
  const selectSwing = useCallback(async (
    sessionId: string,
    runId: string,
    attempt: number
  ): Promise<void> => {
    await callEdgeFunction('demo-run-select', 'POST', {
      demo_session_id: sessionId,
      run_id: runId,
      attempt,
    });
    queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
  }, [queryClient]);

  // DM01-C: Trigger pipeline for selected swing
  const triggerPipeline = useCallback(async (
    sessionId: string
  ): Promise<{ ok: boolean; already_triggered: boolean; run_id: string; triggered_at: string }> => {
    const result = await callEdgeFunction<{
      ok: boolean;
      already_triggered: boolean;
      run_id: string;
      triggered_at: string;
    }>('demo-run-trigger', 'POST', {
      demo_session_id: sessionId,
    });
    queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
    return result;
  }, [queryClient]);

  // DM01-C: Get run status
  const getRunStatus = useCallback(async (
    sessionId: string
  ): Promise<{
    stage: string;
    progress: number;
    statusText: string;
    run_id: string;
    errorMessage?: string;
  }> => {
    const params = new URLSearchParams({ demo_session_id: sessionId });
    return callEdgeFunction('demo-run-status', 'GET', undefined, params);
  }, []);

  // DM01-E: Generate PDF report for session
  const generateReport = useCallback(async (
    sessionId: string
  ): Promise<{ ok: boolean; artifact_id?: string; storage_path?: string }> => {
    const result = await callEdgeFunction<{
      ok: boolean;
      artifact_id?: string;
      storage_path?: string;
    }>('demo-report-generate', 'POST', {
      demo_session_id: sessionId,
    });
    return result;
  }, []);

  // DM01-E: Send email with report and activation link
  const sendEmail = useCallback(async (
    sessionId: string
  ): Promise<{ ok: boolean; email_sent: boolean }> => {
    const result = await callEdgeFunction<{
      ok: boolean;
      email_sent: boolean;
    }>('demo-email-send', 'POST', {
      demo_session_id: sessionId,
    });
    queryClient.invalidateQueries({ queryKey: ['demo-sessions'] });
    return result;
  }, [queryClient]);

  // Check if user can access demo mode.
  // OPS-25: prospects of a demo session may also load the results page.
  const canAccessDemoMode =
    !!configData?.user?.isAdmin ||
    configData?.user?.role === 'demo_host' ||
    configData?.user?.role === 'prospect' ||
    configData?.user?.isProspect === true;
  const isDemoModeEnabled = configData?.config?.enabled ?? false;

  return {
    // Config
    config: configData?.config ?? null,
    events: configData?.events ?? [],
    quota: configData?.quota ?? null,
    user: configData?.user ?? null,
    
    // Status
    isLoading: isLoadingConfig,
    error: configError,
    canAccessDemoMode,
    isDemoModeEnabled,
    
    // Actions
    refetchConfig,
    createSession: createSessionMutation.mutateAsync,
    isCreatingSession: createSessionMutation.isPending,
    createSessionError: createSessionMutation.error,
    
    updateSession: updateSessionMutation.mutateAsync,
    isUpdatingSession: updateSessionMutation.isPending,
    
    submitIntake: submitIntakeMutation.mutateAsync,
    isSubmittingIntake: submitIntakeMutation.isPending,
    submitIntakeError: submitIntakeMutation.error,
    
    createEvent: createEventMutation.mutateAsync,
    isCreatingEvent: createEventMutation.isPending,
    
    getSession,
    
    // DM01-C additions
    selectSwing,
    triggerPipeline,
    getRunStatus,
    
    // DM01-E additions
    generateReport,
    sendEmail,
  };
}

// Constants for intake form
export const TERMS_VERSION = '2026-02';
export const WAIVER_VERSION = '2026-02';
